import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Presentation, Mail } from 'lucide-react';

const DECK_URL = '/LIFE-Link-Training-Deck.pptx';

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.name || 'friend').split(' ')[0];

  return (
    <div className="min-h-screen bg-[#f4f6f8] py-8 px-4" data-testid="welcome-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <main id="main-content" tabIndex={-1} className="max-w-[680px] mx-auto outline-none">
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {/* Header band */}
          <div className="bg-[#34657f] py-7 px-10 text-center">
            <span className="inline-block bg-white rounded-lg px-5 py-2.5">
              <img src="/lifelink-logo.png" alt="LIFE Link" className="h-12 w-auto object-contain" />
            </span>
          </div>
          <div className="h-1.5 bg-[#e87722]" />

          <div className="px-8 sm:px-12 py-9">
            <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-1.5">
              Dear {firstName},
            </h1>
            <p className="text-xl font-bold text-[#b34e00] mb-6">Welcome to the Project LIFE family!</p>

            <div className="space-y-4 text-[#3d4852] text-[15px] leading-relaxed">
              <p>
                We are excited to have you join a growing community of transition professionals across the
                United States and Canada who are committed to creating meaningful opportunities and
                successful futures for young adults.
              </p>
              <p>
                <strong>LIFE Link</strong>, your Project LIFE portal, will serve as your central link to
                program resources, support, collaboration, and connection with the Project LIFE community.
              </p>
              <p>
                The portal was developed in partnership with <strong>Aspiritech</strong>, a nonprofit that
                empowers autistic adults through meaningful technology careers, and made possible through a
                generous grant from the{' '}
                <a href="https://www.meaf.org" target="_blank" rel="noopener noreferrer" className="text-[#34657f] font-bold underline">
                  Mitsubishi Electric America Foundation
                </a>.
              </p>
              <p>
                <strong className="text-[#34657f]">Phase 1 is now live</strong> and includes Project LIFE
                curriculum, program launch materials, job-site resources, and a community forum where you can
                ask questions, share ideas, celebrate intern successes, and connect with Project LIFE teams
                across the U.S. and Canada.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 my-8">
              <a
                href={DECK_URL}
                className="inline-flex items-center gap-2 bg-[#b34e00] hover:bg-[#a84d09] text-white text-sm font-bold rounded-full px-7 py-3.5 transition-colors"
                data-testid="welcome-training-deck-btn"
              >
                <Presentation className="w-4 h-4" aria-hidden="true" />
                VIEW THE PORTAL TRAINING DECK
              </a>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#34657f] hover:bg-[#2a5060] text-white text-sm font-bold rounded-full px-7 h-[46px]"
                data-testid="welcome-enter-portal-btn"
              >
                EXPLORE THE PORTAL
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
              </Button>
            </div>

            <div className="space-y-4 text-[#3d4852] text-[15px] leading-relaxed">
              <p>
                <strong className="text-[#3d7a22]">Coming in Phase 2</strong>, the portal will include tools
                for tracking intern progress in the curriculum and on job sites, providing valuable
                quantitative and qualitative data to support IEP teams and transition planning.
              </p>
              <p>
                Most importantly, remember that the portal is more than a collection of resources&mdash;it is
                your connection to the Project LIFE community. We encourage you to explore, connect, share,
                and reach out whenever you need support.
              </p>
              <p className="flex items-center gap-2 flex-wrap">
                <Mail className="w-4 h-4 text-[#34657f] shrink-0" aria-hidden="true" />
                Questions? Contact the Project LIFE National Team at{' '}
                <a href="mailto:projectlife@butlertech.org" className="text-[#34657f] font-bold underline">
                  projectlife@butlertech.org
                </a>
              </p>
              <p>We are so glad you're here and can't wait to see what your team and interns accomplish!</p>
              <p className="text-lg font-bold text-[#b34e00]">Welcome to Project LIFE!</p>
            </div>
          </div>

          <div className="bg-[#f8fafc] border-t border-slate-200 px-10 py-5 text-center text-xs text-[#64707a] leading-relaxed">
            LIFE Link | Project LIFE | Butler Tech
          </div>
        </div>
      </main>
    </div>
  );
}
